import { IconButton, IIconProps, Label, Spinner, Stack, ActionButton, TooltipHost } from "@fluentui/react";
import React, { useCallback, useState } from "react";
import CosmicCostTree from "./CosmicCostTree";
import styles from "./CostHierarchyTable.less";
import { SectionHeader } from "../../common/SectionHeader";
import { useCosmicCostHierarchy } from "../../../hooks/useCosmicCostHierarchy";
import html2canvas from "html2canvas";
import { EventType, LogComponent, LogElement, LogTarget } from "../../../models/LogModel";
import { useBoolean } from "@fluentui/react-hooks";
import { trackEventCallback } from "../../../utils/AppInsights";
import { useFlights } from "../../../hooks/useSettings";
import { useMsal } from "@azure/msal-react";
import FeedbackPanel from "../../feedback/FeedbackPanel";
import { CosmicService } from "../../../services/cosmicService";
import { useDateRange } from "../../../hooks/useDateSelector";
import { useCategoryFilters } from "../../../hooks/useFilters";
import { useCosmicFilters } from "../../../hooks/useCosmicFilters";
import { useSelector } from "react-redux";
import { IAppState } from "../../../store";
import { ServiceTreeItem } from "../../../models/serviceTree";

interface CostHierarchyTableProps {
    selectedMonth: string ;
}
const feedbackIconProps: IIconProps = { iconName: "Feedback" };

function formatNumber(value: number): string {
    if (Math.abs(value) >= 1_000_000) {
        return (value / 1_000_000).toFixed(2).replace(/\.00$/, '') + 'M';
    }
    if (Math.abs(value) >= 1_000) {
        return (value / 1_000).toFixed(2).replace(/\.00$/, '') + 'K';
    }
    return value.toFixed(2).replace(/\.00$/, '');
}

const CostHierarchyTable: React.FC<CostHierarchyTableProps> = ({ selectedMonth }) => {
    const { data: hierarchyData, isLoading, query, clear, error } = useCosmicCostHierarchy(selectedMonth);
    const [, setSelectedWorkloads] = useState<string[]>([]);

    // Additional hooks for downloading
    const [isDownloadingEnabled, {setFalse: disableDownloading, setTrue: enableDownloading}] = useBoolean(true);
    const globalFilters = useCategoryFilters().filters;
    const cosmicSearchFilters = useCosmicFilters('defaultFilterKey').cosmicSearchFilters;
    const {startDate, endDate} = useDateRange()
    const serviceIdMap = useSelector<IAppState, Map<string, ServiceTreeItem>>((state) => state.serviceTree.indexMap);

    const handleDownload = useCallback(async () => {
        disableDownloading();

        const response = await CosmicService.downloadCosmicCostHierarchy(
            startDate,
            endDate,
            selectedMonth,
            "CosmicCostHierarchy",
            cosmicSearchFilters || '',
            globalFilters.filters,
            serviceIdMap,
        );

        const data = response.data;
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.fileName;
        a.click();
        window.URL.revokeObjectURL(url);

        enableDownloading();
    }, [disableDownloading, enableDownloading, startDate, endDate, selectedMonth, cosmicSearchFilters, globalFilters.filters, serviceIdMap]);

    const handleWorkloadSelectionChange = async (ids: string[], deselect = false) => {
        setSelectedWorkloads(ids);
        if (!deselect) {
            await query(ids[0]);
        }
        else {
            await clear(ids[0]);
        }
    };

    // User Feedback
    const { instance } = useMsal();
    const { data: flights } = useFlights();
    const account = instance.getActiveAccount();
    const [isFeedbackPanelOpen, { setTrue: openFeedbackPanel, setFalse: dismissFeedbackPanel }] = useBoolean(false);
    const [currentURL, setCurrentURL] = useState<string>('');
    const [currentScreenshotURL, setCurrentScreenshotURL] = useState<string>('');
    const captureScreenshot = async (title: string) => {
        trackEventCallback(LogComponent.CosmicCrossChargeView, LogElement.CosmicCostHierarchyTable, "Send Feedback Chart", LogTarget.Button, undefined, EventType.Click);

        const url = window.location.href;
        setCurrentURL(url);

        await openFeedbackPanel();

        const targetElement = document.getElementById(`FeedbackSection_${title}`);

        if (targetElement) {
            setTimeout(() => {
                html2canvas(targetElement).then((canvas) => {
                    const screenshotDataUrl = canvas.toDataURL('image/png');
                    setCurrentScreenshotURL(screenshotDataUrl);
                });
            }, 100);
        }
    };
    const handleDismissFeedbackPanel = () => {
        setCurrentScreenshotURL('');
        dismissFeedbackPanel();
    };

    if (error) {
        return (
            <div className={styles.error}>
                Error loading workload data: {error.message}
            </div>
        );
    }

    return (
        <div className={styles.cardContainer} id={`FeedbackSection_CosmicCostHierarchyTable`}>
            {flights?.enableUserFeedback &&
                <IconButton
                    title="Send feedback"
                    className={styles.feedbackIcon}
                    iconProps={feedbackIconProps}
                    onClick={() => captureScreenshot("CosmicCostHierarchyTable")}
                />}
            <SectionHeader
                title="Cost details per Workload and Namespace"
                extra={(
                    <Stack horizontal tokens={{ childrenGap: 40 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <TooltipHost content="Downloading all data for the selected date range may take a few minutes. Please wait.">
                                <ActionButton onClick={handleDownload} iconProps={{ iconName: 'Download' }} text={isDownloadingEnabled ? "Download All" : "Downloading"} disabled={!isDownloadingEnabled} />
                            </TooltipHost>
                            {isLoading ? null : <Label>Total Cost</Label>}
                            {isLoading ? null : <Label>{`${formatNumber(hierarchyData?.totalCost || 0)}`}</Label>}
                        </Stack>
                    </Stack>
                )}
            />
            <Stack>
                {isLoading ? (
                    <div className={styles.loading}>
                        <Spinner label="Loading cost data..." />
                    </div>
                ) : (
                    <CosmicCostTree
                        data={hierarchyData?.data || []}
                        onSelectionChange={handleWorkloadSelectionChange}
                    />
                )}
            </Stack>
            <FeedbackPanel
                isPanelOpen={isFeedbackPanelOpen}
                onDismiss={handleDismissFeedbackPanel}
                userName={account?.username}
                currentURL={currentURL}
                screenshotInputURL={currentScreenshotURL}
                FeedbackTab={LogComponent.CosmicCostHierarchyTable}
            />
        </div>
    );
};

export default CostHierarchyTable;